/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// tslint:disable:no-reference
/// <reference path="types/MicrosoftMaps/Microsoft.Maps.All.d.ts" />

import { IFullOrgUnitAvailability } from '@msdyn365-commerce-modules/retail-actions/dist/types/utilities/full-org-unit-availability';
import { IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';
import classname from 'classnames';
import { observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IMapCustomData } from './map-custom.data';
import { IMapCustomProps, IPushpinOptionsData } from './map-custom.props.autogenerated';

export interface IHeader {
    headerProps: INodeProps;
    heading: React.ReactNode;
}

export interface IMapViewProps extends IMapCustomProps<IMapCustomData> {
    ModuleProps: IModuleProps;
    Header: IHeader;
    MapProps: INodeProps;
    Map: Microsoft.Maps.Map | undefined;
}

/**
 *
 * Map component
 * @extends {React.Component<IMapCustomProps<IMapCustomData>>}
 */
@observer
class Map extends React.Component<IMapCustomProps<IMapCustomData>> {
    @observable public map: Microsoft.Maps.Map | undefined;
    private mapRef: React.RefObject<HTMLElement> = React.createRef<HTMLElement>();

    constructor(props: IMapCustomProps<IMapCustomData>) {
        super(props);
    }

    public componentDidMount(): void {
        const {
            context: {
                telemetry,
                actionContext: {
                    requestContext: { channel }
                }
            }
        } = this.props;

        if (channel && !channel.BingMapsApiKey) {
            telemetry.error('BingMapsApiKey is missing.');
            return;
        }

        if (channel && !channel.BingMapsEnabled) {
            telemetry.error('Map is disabled from HQ.');
            return;
        }

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.setMapModuleLoaded,
            () => {
                this.props.data.storeSelectorStateManager.result?.setMapModuleLoaded(true);
            }
        );

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.loadMapApi,
            () => {
                this.props.data.storeSelectorStateManager.result?.loadMapApi({
                    key: channel?.BingMapsApiKey,
                    lang: this.props.context?.actionContext?.requestContext?.channel?.ChannelCountryRegionISOCode,
                    market: this.props.context?.actionContext?.requestContext?.channel?.DefaultLanguageId
                });
            }
        );

        reaction(
            () => this.props.data.storeSelectorStateManager.result?.isMapApiLoaded,
            () => {
                this._initMap();
            }
        );

        reaction(
            () => {
                return [
                    this.props.data.storeSelectorStateManager.result?.context?.orgUnitStoreInformation,
                    this.props.data.storeSelectorStateManager.result?.selectedStoreLocationId,
                    this.map
                ];
            },
            async () => {
                if (!this.props.data.storeSelectorStateManager.result?.context?.isDialog) {
                    this._updateMap();
                }
            }
        );
    }

    public render(): JSX.Element | null {
        const {
            config: { heading, className },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;

        const displayMap = storeSelectorStateManager?.listMapViewState?.displayMap;

        const viewProps: IMapViewProps = {
            ...this.props,
            ModuleProps: {
                tag: 'div',
                moduleProps: this.props,
                className: classname('ms-map', { show: displayMap }, className)
            },
            Header: {
                headerProps: { className: 'ms-map__header' },
                heading: heading && heading.text && (
                    <Msdyn365.Text
                        className='ms-map__heading'
                        {...heading}
                        tag={heading.tag || 'h2'}
                        text={heading.text}
                        editProps={{
                            onEdit: this.handleHeadingChange,
                            requestContext: this.props.context.request
                        }}
                    />
                )
            },
            MapProps: {
                tag: 'div',
                className: 'ms-map__body',
                ref: this.mapRef
            },
            Map: this.map
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading!.text = event.target.value);

    private _initMap = () => {
        const {
            context: {
                actionContext: {
                    requestContext: { channel }
                }
            },
            data: {
                storeSelectorStateManager: { result: storeSelectorStateManager }
            }
        } = this.props;
        if (storeSelectorStateManager?.isMapApiLoaded) {
            this.map = new Microsoft.Maps.Map(this.mapRef.current as HTMLElement, {
                credentials: channel?.BingMapsApiKey,
                pushpinAccessible: true
            });
        }
    };

    private _updateMap = () => {
        // const pushpinOptions = this.props.config.pushpinOptions;
        const storeSelectorStateManager = this.props.data.storeSelectorStateManager?.result;
        const orgUnitStoreInformation = storeSelectorStateManager?.context?.orgUnitStoreInformation;
        const selectedStoreLocationId = storeSelectorStateManager?.selectedStoreLocationId;

        if (this.map && orgUnitStoreInformation) {
            let mapLocation: OrgUnitLocation | undefined;
            this.map.entities.clear();
            const pushpins: Microsoft.Maps.IPrimitive[] = [];

            orgUnitStoreInformation.map((unitStoreInfo: IFullOrgUnitAvailability, index: number) => {
                const storeLocation = unitStoreInfo?.OrgUnitAvailability?.OrgUnitLocation;

                if (storeLocation?.Latitude && storeLocation.Longitude) {
                    const isSelectedLocation = selectedStoreLocationId === storeLocation.OrgUnitNumber;
                    // tslint:disable-next-line:no-shadowed-variable
                    const pushpinOptions = {
                        color: storeLocation.OrgUnitName && storeLocation.OrgUnitName.startsWith('R ') ? '#5f8798' : '#8c7760',
                        selectionColor: storeLocation.OrgUnitName && storeLocation.OrgUnitName.startsWith('R ') ? '#004f71' : '#542D00',
                        showIndex: true,
                        size: 1.5
                    };
                    const options = this._getPushpinOptions(isSelectedLocation, index, pushpinOptions);
                    const pushpin = new Microsoft.Maps.Pushpin(
                        new Microsoft.Maps.Location(storeLocation.Latitude, storeLocation.Longitude),
                        options
                    );

                    Microsoft.Maps.Events.addHandler(
                        pushpin,
                        'click',
                        () =>
                            storeLocation.OrgUnitNumber &&
                            storeSelectorStateManager?.setSelectedStoreLocationId(storeLocation.OrgUnitNumber)
                    );

                    isSelectedLocation ? pushpins.unshift(pushpin) : pushpins.push(pushpin);

                    if (isSelectedLocation) {
                        mapLocation = storeLocation;
                    }
                }
            });

            if (mapLocation) {
                const currLocation = new Microsoft.Maps.Location(mapLocation.Latitude, mapLocation.Longitude);
                this.map.setView({ center: currLocation });
            } else {
                // Create a LocationRect from array of pushpins and set the map view.
                this.map.setView({
                    bounds: Microsoft.Maps.LocationRect.fromShapes(pushpins)
                });
            }

            this.map.setOptions({ pushpinAccessible: true });
            this.map?.entities.push(pushpins);
        }
    };

    private _getPushpinOptions = (isSelectedLocation: boolean, index: number, pushpinOptions?: IPushpinOptionsData) => {
        const text = pushpinOptions?.showIndex ? (index + 1).toString() : undefined;

        const size = pushpinOptions?.size || 1;
        const color = isSelectedLocation ? pushpinOptions?.selectionColor || pushpinOptions?.color : pushpinOptions?.color;
        const icon = this._getSvgIcon(size, color, text);

        return {
            // fallback if icon doesn't render properly
            color: isSelectedLocation ? pushpinOptions?.selectionColor : pushpinOptions?.color,
            icon: icon
        };
    };

    private _getSvgIcon = (size: number, color?: string, text?: string) => {
        return `<svg width="37" height="33" viewBox="0 0 37 33" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.8474 32.8273L17.8388 32.8217L17.8085 32.8014C17.7826 32.784 17.7452 32.7587 17.6972 32.7258C17.6012 32.6601 17.4626 32.5639 17.2874 32.4388C16.9372 32.1887 16.4403 31.8229 15.8455 31.3547C14.6576 30.4195 13.0709 29.0691 11.4806 27.4095C8.34366 24.136 5 19.4606 5 14.2788C5 10.7532 6.41674 7.37441 8.93467 4.88509C11.4522 2.39616 14.8642 1 18.4195 1C21.9748 1 25.3868 2.39616 27.9044 4.88509C30.4223 7.37441 31.839 10.7532 31.839 14.2788C31.839 19.4606 28.4954 24.136 25.3585 27.4095C23.7682 29.0691 22.1815 30.4195 20.9936 31.3547C20.3988 31.8229 19.9019 32.1887 19.5516 32.4388C19.3765 32.5639 19.2378 32.6601 19.1418 32.7258C19.0938 32.7587 19.0565 32.784 19.0305 32.8014L19.0002 32.8217L18.9917 32.8273L18.9882 32.8296C18.6435 33.0568 18.1955 33.0568 17.8509 32.8296L17.8474 32.8273Z" fill="${color}"/>
            ${
                text
                    ? `<text x="19" y="18" style="font-size:12px;fill:#ffffff; font-family: Arial, sans-serif; font-weight:bold;" text-anchor="middle">${text}</text>`
                    : ''
            }
        </svg>`;
    };
}

export default Map;
